import React from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { VISIBLE_SOCIAL_LINKS } from '../../constants/contactLinks';
import { useTheme } from '../../contexts/ThemeContext';

const SocialIcons = ({ className = 'text-ink-subtle', buttonClassName = '' }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3">
      {VISIBLE_SOCIAL_LINKS.map((item) => (
        <a
          key={item.key}
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex h-10 w-10 items-center justify-center rounded-full border transition-colors ${className} ${item.hoverClass} ${buttonClassName}`.trim()}
          aria-label={t(item.labelKey)}
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d={item.path} />
          </svg>
        </a>
      ))}
    </div>
  );
};

const PromotionalFooter = () => {
  const { t } = useTranslation();
  const linkClass =
    'text-sm text-white/85 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded px-1';

  const navLinks = [
    { to: '/', label: t('navigation.home') },
    { to: '/team', label: t('promotionalHome.team') },
    { to: '/press-releases', label: t('navigation.pressReleases') },
    { to: '/jupas-calculator', label: t('promotionalHome.jupasCalculator') },
    { to: '/login', label: t('promotionalHome.loginSignup') },
  ];

  return (
    <footer className="bg-[#121212] text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        <div className="flex flex-col md:flex-row md:justify-between gap-10">
          <div className="max-w-xl space-y-5">
            <p className="text-sm text-white/75 leading-relaxed">
              © {new Date().getFullYear()} {t('common.companyName')}
              {t('common.allRightsReserved')}
              <a
                href="https://www.atlab.hku.hk"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:text-white transition-colors no-underline"
              >
                {t('common.artsTechnologyLab')}
              </a>
              {t('common.supportedBy')}
            </p>
            <div>
              <p className="text-xs font-medium text-white/60 uppercase tracking-wide mb-3">
                {t('footer.followUs')}
              </p>
              <SocialIcons
                className="text-white/70"
                buttonClassName="border-white/20 bg-white/5 hover:bg-white/10 hover:border-white/30"
              />
            </div>
          </div>

          <nav aria-label={t('promotionalHome.footerNavLabel')}>
            <ul className="flex flex-col items-start md:items-end gap-2">
              {navLinks.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className={linkClass}>
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
    </footer>
  );
};

const DefaultFooter = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();

  return (
    <footer className="bg-surface border-t border-edge">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left w-full md:w-auto">
            <Link to="/" className="font-roboto font-medium flex-shrink-0">
              <img
                src={isDark ? '/asset/icons/logo-mono.svg' : '/asset/icons/logo-v3.png'}
                alt={t('common.companyLogo')}
                className={`${isDark ? 'h-9 w-9' : 'h-12 w-12'} mb-1 mx-auto sm:mx-0`}
              />
            </Link>
            <div className="flex flex-col gap-1 items-center sm:items-start">
              <p className="text-sm text-ink-muted">
                <Trans
                  i18nKey="footer.picsNotice"
                  components={{
                    privacyLink: (
                      <Link to="/privacy" className="text-link hover:text-link-hover no-underline" />
                    ),
                  }}
                />
              </p>
              <p className="text-sm text-ink-subtle">
                © {new Date().getFullYear()} {t('common.companyName')}
                {t('common.allRightsReserved')}
                <a
                  href="https://www.atlab.hku.hk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-link hover:text-link-hover no-underline"
                >
                  {t('common.artsTechnologyLab')}
                </a>
                {t('common.supportedBy')}
              </p>
            </div>
          </div>

          <div className="flex flex-col items-center md:items-end gap-3">
            <p className="text-xs font-medium text-ink-subtle uppercase tracking-wide">{t('footer.followUs')}</p>
            <SocialIcons buttonClassName="border-edge bg-surface-muted hover:border-edge-strong hover:bg-surface" />
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 w-full md:w-auto">
            <a
              href="mailto:info4edvise@gmail.com"
              className="inline-flex items-center rounded-full border border-edge bg-surface-muted px-4 py-2 text-sm font-medium text-ink-muted transition-colors hover:border-edge-strong hover:bg-surface hover:text-ink"
            >
              {t('common.contactEmail')}
            </a>
            <Link
              to="/tutorials"
              className="inline-flex items-center rounded-full border border-transparent px-4 py-2 text-sm font-medium text-primary-foreground bg-primary hover:bg-primary-hover transition-colors"
            >
              {t('common.helpCentre')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

/**
 * @param {{ variant?: 'default' | 'promotional' }} props
 */
const Footer = ({ variant = 'default' }) => {
  if (variant === 'promotional') {
    return <PromotionalFooter />;
  }
  return <DefaultFooter />;
};

export default Footer;
